import React, { useCallback, useEffect, useReducer, useState } from "react"
import {
    obterDadosPromocaoFidelidade,
    salvarPromocaoFidelidade,
} from "../../../services/promocaoFidelidade"
import useLoadingStore from "../../../stores/LoadingStore"
import { Loading } from "../../RegistradorDePedidos"
import {
    CadastroPromocaoFidelidadeInicial,
    modeloDoBackend,
} from "../models/cadastroPromocaoFidelidadeModel"
import { CadastroPromocaoFidelidadeActionsTypes } from "./cadastroPromocaoFidelidadeActions"
import { CadastroPromocaoFidelidadeContext } from "./CadastroPromocaoFidelidadeContext"
import { cadastroPromocaoFidelidadeReducer } from "./cadastroPromocaoFidelidadeReducer"

export function CadastroPromocaoFidelidadeContextProvider({
    children,
}: {
    children: React.ReactNode
}) {
    const [dados, dispatch] = useReducer(
        cadastroPromocaoFidelidadeReducer,
        CadastroPromocaoFidelidadeInicial
    )
    const [erroCarregamento, setErroCarregamento] = useState<string | null>(null)
    const setLoading = useLoadingStore(s => s.setLoading)

    const carregar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return

        try {
            setLoading(true)
            const response = await obterDadosPromocaoFidelidade(autorizacao)
            if ("retornoErro" in response) {
                setErroCarregamento(response.mensagem || "Erro ao carregar promoção de fidelidade")
            } else {
                setErroCarregamento(null)
                dispatch({
                    type: CadastroPromocaoFidelidadeActionsTypes.CARREGAR,
                    payload: modeloDoBackend(response),
                })
            }
        } catch {
            setErroCarregamento("Erro ao carregar promoção de fidelidade")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        carregar()
    }, [])

    const salvar = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return

        try {
            setLoading(true)
            const payload = {
                premio: dados.premio,
                tipoPontuacao: dados.tipoPontuacao,
                valorPorSelo: dados.tipoPontuacao === 0 ? dados.valorPorSelo || null : null,
                tipoPremio: dados.tipoPremio,
                valorPremio: dados.tipoPremio === 0 ? dados.valorPremio || null : null,
                valorPremioPercentualMedia:
                    dados.tipoPremio === 1 ? dados.valorPremioPercentualMedia || null : null,
                valorPremioPercentual:
                    dados.tipoPremio === 2 ? dados.valorPremioPercentual || null : null,
                consumoMinimo: !dados.possuiDelivery ? dados.consumoMinimo || null : null,
                quantidadeNecessaria: Number(dados.quantidadeNecessaria),
                valorConsumoMinimoParaSelo: dados.valorConsumoMinimoParaSelo || null,
                observacao: dados.observacao,
                exibirPublicamente: dados.exibirPublicamente,
                naoPermitirFidelidadeRetiradaLocal: dados.naoPermitirFidelidadeRetiradaLocal,
                regulamentoTexto: dados.regulamentoTexto,
                bonificarIndicacaoAmigos: dados.bonificarIndicacaoAmigos,
                bonificarPesquisaSatisfacao: dados.bonificarPesquisaSatisfacao,
                ignorarPedioMinimoNoResgate: dados.ignorarPedioMinimoNoResgate,
                diasValidadeCartela: Number(dados.diasValidadeCartela),
                diasRenovacao: Number(dados.diasRenovacao),
                fatorMultiplicadorSelos: Number(dados.fatorMultiplicadorSelos),
                maximoRegistroSelosImpressosDiarios: Number(
                    dados.maximoRegistroSelosImpressosDiarios
                ),
                maximoRegistroSelosVirtuaisDiarios: Number(
                    dados.maximoRegistroSelosVirtuaisDiarios
                ),
                permiteResgateAnonimo: dados.permiteResgateAnonimo,
                validarMaximoDiarioDelivery: dados.validarMaximoDiarioDelivery,
                freteGratisCartelaCompleta: dados.freteGratisCartelaCompleta,
                acumularSaldoRestante: dados.acumularSaldoRestante,
            }

            const response = await salvarPromocaoFidelidade(autorizacao, payload)
            if ("retornoErro" in response) {
                throw new Error(response.mensagem)
            }
        } finally {
            setLoading(false)
        }
    }, [dados])

    return (
        <CadastroPromocaoFidelidadeContext.Provider
            value={{ dados, dispatch, salvar, erroCarregamento }}
        >
            <Loading />
            {children}
        </CadastroPromocaoFidelidadeContext.Provider>
    )
}
